import {appStore, handleImpersonationExpired} from "@store/appStore";
import { navigationStore } from "@store/navigationStore";
import { navigate } from "../../navigate";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface HttpOptions<TBody = unknown> {
    method?: HttpMethod;
    body?: TBody;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    auth?: boolean; // include Authorization header (default: true)
    responseType?: "json" | "text" | "blob"; // default: "json"
    cache?: RequestCache;
}

/**
 * The server's `detail` as text. A Ninja validation error (422) sends a list of
 * `{loc, msg}`; as an Error message that list read "[object Object]".
 */
export function detailText(detail: unknown): string | undefined {
    if (!Array.isArray(detail)) return detail as string | undefined;
    return detail
        .map((item) => {
            const msg = String(item?.msg ?? item).replace(/^Value error, /, "");
            const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : undefined;
            return typeof field === "string" && !msg.includes(field) ? `${field}: ${msg}` : msg;
        })
        .join("; ");
}

export async function httpRequest<TResponse = unknown, TBody = unknown>(
    url: string,
    { method = "GET", body, headers = {}, signal, auth = true, responseType = "json", cache }: HttpOptions<TBody> = {}
): Promise<TResponse> {
    const baseHeaders: Record<string, string> = {
        "Content-Type": "application/json",
        ...headers,
    };

    if (auth) {
        baseHeaders.Authorization = `Token ${appStore.authToken}`;
    }

    const fetchOptions: RequestInit = {
        method,
        headers: baseHeaders,
        signal,
        cache: cache ?? 'no-cache',
    };

    if (body !== undefined && method !== "GET") {
        const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
        if (isFormData) {
            // Let the browser set multipart boundary
            delete (baseHeaders as any)["Content-Type"];
            fetchOptions.body = body as unknown as BodyInit;
        } else {
            fetchOptions.body = typeof body === "string" ? (body as string) : JSON.stringify(body);
        }
    }

    const res = await fetch(url, fetchOptions);

    // 401 means auth itself failed — token missing/expired. Bounce to login.
    // 403 means authenticated-but-forbidden; let the caller surface it.
    if (res.status == 401) {
        if (appStore.viewingAsUser && appStore.realToken) {
            await handleImpersonationExpired();
            throw new Error("Impersonation expired");
        }
        // Capture current URL so login can send the user back after re-auth.
        // AuthGate only runs on initial mount, so mid-session 401s must save here.
        const current = new URL(window.location.href);
        const redirect = `${current.pathname}${current.search}${current.hash}`;
        if (redirect !== "/" && redirect !== "/request-change-password") {
            navigationStore.postLoginRedirect = redirect;
        }
        appStore.authToken = '';
        navigate(`${import.meta.env.BASE_URL}`);
    }

    if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        const message = (errorData && (detailText(errorData.detail) || errorData.message || errorData.error)) || res.statusText;
        // Carry the machine code + HTTP status on the error object (message stays
        // unchanged) so callers that want to surface them can, without every other
        // caller's error text changing.
        const err = new Error(message) as Error & { code?: string; status?: number; body?: unknown };
        if (errorData?.error || errorData?.code) err.code = errorData.error || errorData.code;
        err.status = res.status;
        // The whole parsed body, for callers that need `details` (e.g. the banner overlap 409).
        err.body = errorData;
        throw err;
    }

    if (responseType === "text") {
        const text = await res.text();
        return text as TResponse;
    }

    if (responseType === "blob") {
        const blob = await res.blob();
        return blob as TResponse;
    }

    const data = await res.json().catch(() => null);
    return data as TResponse;
}


